import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createBooking, getAllBookings } from "../store/slices/bookingSlice";
import { fetchAllUsers } from "../store/slices/authSlice";
import { fetchSocieties } from "../store/slices/societySlice";
import { useSnackbar } from "notistack";
import { toWords } from "number-to-words";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { ChevronLeft, ChevronRight, Filter, Search, X, Calendar } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactDOM from "react-dom";
import openInstallmentPlanPrint from "./InstallmentPlanPrint";

// Booking Modal Component
const BookingModal = ({
  isOpen,
  onClose,
  plotId,
  selectedPlot,
  activeSociety,
  theme,
  gradient,
  users,
  searchQuery,
  setSearchQuery,
  selectedCustomer,
  setSelectedCustomer,
  isDropdownOpen,
  setIsDropdownOpen,
  filteredUsers,
  formattedAmount,
  setFormattedAmount,
  amountInWords,
  setAmountInWords,
  dealerCommissionFormatted,
  setDealerCommissionFormatted,
  formattedInitialPayment,
  setFormattedInitialPayment,
  formattedHoldAmount,
  setFormattedHoldAmount,
  handlePlotAmountChange,
  handleInitialPaymentChange,
  handleDealerCommissionChange,
  handleHoldAmountChange,
  handleSelectCustomer,
  formatPhoneNumber,
  formatCurrency,
  calculateRemainingBalance,
  stringToLocalDate,
  dateToString,
  resetAllFields,
  calculateDraftInstallmentPlan,
  BookingValidationSchema,
  dispatch,
  enqueueSnackbar,
  createBooking,
  getAllBookings,
  setActiveSociety,
  setLocalPlots,
  localPlots,
}) => {
  // Initialize formatted amount when plot is selected
  useEffect(() => {
    if (isOpen && selectedPlot?.price && !formattedAmount) {
      const priceValue = selectedPlot.price.toString();
      setFormattedAmount(
        priceValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      );
      const words = toWords(Number(priceValue));
      setAmountInWords(
        `${priceValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} (${
          words.charAt(0).toUpperCase() + words.slice(1)
        })`
      );
    }
  }, [isOpen, selectedPlot, formattedAmount, setFormattedAmount, setAmountInWords]);

  if (!isOpen) return null;

  const draftInstallmentPlan = (values) => {
    if (values?.paymentMode === "Installment") {
      return calculateDraftInstallmentPlan(values);
    }
    return [];
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-[110] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`w-full max-w-5xl mx-auto rounded-xl shadow-2xl ${gradient} border ${theme.tailwind.border2} ${theme.tailwind.bg12} max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${theme.tailwind.border13} ${theme.tailwind.bg3}`}
        >
          <div>
            <h2
              className={`text-3xl font-bold ${theme.tailwind.text8} uppercase tracking-wide`}
            >
              Create Booking - Plot {selectedPlot?.plot_number || ""}
            </h2>
            <p className={`${theme.tailwind.text9} text-sm mt-1`}>
              {selectedPlot?.block && `Block: ${selectedPlot.block}`}
              {selectedPlot?.marla && ` | Marla: ${selectedPlot.marla}`}
              {selectedPlot?.category && ` | Category: ${selectedPlot.category}`}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className={`bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all hover:rotate-90 ${theme.tailwind.text9}`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div
          className={`flex-1 overflow-y-auto p-6 ${theme.tailwind.bg12}`}
          style={{ maxHeight: "calc(90vh - 120px)" }}
        >
          <Formik
            initialValues={{
              plot: plotId,
              customer: "",
              paymentMode: "Full",
              bookingAmount: selectedPlot?.price?.toString() || "",
              dealerCommission: "",
              dealerPhone: "+92 ",
              dealerName: "",
              initialPayment: "",
              installmentYears: 0,
              installmentMonths: 0,
              receiptNo: "",
              offerTag: "",
              bookingDate: "",
              dealerCommissionDate: "",
              holdAmount: "",
              holdDays: "",
            }}
            validationSchema={BookingValidationSchema}
            onSubmit={async (
              values,
              { resetForm, setSubmitting, setFieldValue }
            ) => {
              try {
                const formatDateForAPI = (dateValue) => {
                  if (!dateValue) return "";
                  try {
                    let dateObj;
                    if (dateValue instanceof Date) {
                      dateObj = dateValue;
                    } else if (typeof dateValue === "string" && dateValue.includes("/")) {
                      dateObj = stringToLocalDate(dateValue);
                    } else {
                      dateObj = new Date(dateValue);
                    }

                    if (!dateObj || isNaN(dateObj.getTime())) return "";

                    // Format as YYYY-MM-DD in local timezone to avoid UTC conversion
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  } catch (error) {
                    console.error("Error formatting date for API:", error, dateValue);
                    return "";
                  }
                };

                const bookingDate = formatDateForAPI(values.bookingDate);
                const dealerCommissionDate = formatDateForAPI(
                  values.dealerCommissionDate
                );
                const bookingData = {
                  plot_id: values.plot,
                  customer_id: values.customer,
                  total_amount: parseFloat(values.bookingAmount),
                  dealer_comission: parseFloat(values.dealerCommission),
                  dealer_name: values.dealerName,
                  dealer_phone: values.dealerPhone,
                  initial_payment: parseFloat(values.initialPayment) || 0,
                  payment_mode: values.paymentMode,
                  installment_years:
                    values.paymentMode === "Installment"
                      ? parseFloat(values.installmentYears || 0)
                      : 0,
                  installment_months:
                    values.paymentMode === "Installment"
                      ? parseFloat(values.installmentMonths || 0)
                      : 0,
                  receipt_no: values.receiptNo,
                  booking_date: bookingDate,
                  dealer_commission_date: dealerCommissionDate,
                  offertag: values.offerTag,
                  hold_amount:
                    values.paymentMode === "Hold"
                      ? parseFloat(values.holdAmount)
                      : 0,
                  hold_days:
                    values.paymentMode === "Hold"
                      ? parseInt(values.holdDays)
                      : 0,
                };

                const createdBooking = await dispatch(
                  createBooking(bookingData)
                ).unwrap();

                if (createdBooking && createdBooking.booking) {
                  enqueueSnackbar("Booking created successfully!", {
                    variant: "success",
                  });
                  if (
                    createdBooking.booking.plot_id &&
                    createdBooking.booking.plot_id.society_id
                  ) {
                    setActiveSociety({
                      _id: createdBooking.booking.plot_id.society_id,
                    });
                  }
                  // Update plot status based on payment mode from created booking
                  const paymentMode = createdBooking.booking.payment_mode || values.paymentMode;
                  const bookingStatus = paymentMode === 'Hold' 
                    ? 'Hold' 
                    : paymentMode === 'Full' 
                    ? 'Sold' 
                    : 'Booked';
                  // Note: status field enum doesn't include 'Hold', so for hold bookings we use 'Available'
                  const plotStatus = paymentMode === 'Hold'
                    ? 'Available' // Status enum doesn't include 'Hold', so use 'Available'
                    : paymentMode === 'Full'
                    ? 'Sold'
                    : paymentMode === 'Installment'
                    ? 'Installment'
                    : 'Booked';
                  
                  setLocalPlots((prevPlots) => {
                    const updatedPlots = prevPlots.map((plot) =>
                      plot._id === values.plot
                        ? {
                            ...plot,
                            booking_status: bookingStatus,
                            status: plotStatus,
                          }
                        : plot
                    );
                    return updatedPlots.sort(
                      (a, b) => a.plot_number - b.plot_number
                    );
                  });
                  await dispatch(getAllBookings());
                  resetAllFields(resetForm, setFieldValue);
                  onClose();
                }
              } catch (err) {
                enqueueSnackbar(`Error: ${err.message || err}`, {
                  variant: "error",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting, errors, touched }) => {
              const installments = draftInstallmentPlan(values);

              return (
                <Form className="space-y-6">
                  {/* Alert Message */}
                  {selectedPlot &&
                    (selectedPlot.category === "Corner" ||
                      selectedPlot.category === "Boulevard") && (
                      <div className="p-4 rounded-lg border border-yellow-200 bg-yellow-50/80 shadow-sm">
                        <p className="text-yellow-800 text-sm font-medium">
                          This plot is categorized as {selectedPlot.category}. An
                          additional 10% charge will be added to the total amount.
                        </p>
                      </div>
                    )}

                  {/* Plot ID Field */}
                  <div>
                    <label
                      className={`block ${theme.tailwind.text9} font-medium`}
                    >
                      Plot ID:
                    </label>
                    <Field
                      type="text"
                      name="plot"
                      readOnly
                      value={values.plot}
                      className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md cursor-not-allowed focus:bg-white focus:text-black`}
                    />
                    <ErrorMessage
                      name="plot"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  {/* Customer Selection */}
                  <div>
                    <label
                      className={`block ${theme.tailwind.text9} font-medium`}
                    >
                      Customer:
                    </label>
                    <Field
                      type="text"
                      name="customerSearch"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsDropdownOpen(true);
                      }}
                      onFocus={() => setIsDropdownOpen(true)}
                      onBlur={() =>
                        setTimeout(() => setIsDropdownOpen(false), 200)
                      }
                      placeholder="Search customer"
                      className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                    />
                    <ErrorMessage
                      name="customer"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />

                    {/* Customer Dropdown */}
                    {isDropdownOpen && filteredUsers.length > 0 && (
                      <div
                        className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg z-50 relative`}
                      >
                        {filteredUsers.slice(0, 10).map((user) => (
                          <div
                            key={user._id}
                            onClick={() =>
                              handleSelectCustomer(user, setFieldValue)
                            }
                            className={`px-4 py-2 cursor-pointer ${theme.tailwind.bgHover5}`}
                          >
                            {user.username}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Payment Mode Selection */}
                  <div>
                    <label
                      className={`block ${theme.tailwind.text9} font-medium`}
                    >
                      Payment Mode:
                    </label>
                    <Field
                      as="select"
                      name="paymentMode"
                      className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                    >
                      <option value="Full">Full</option>
                      <option value="Installment">Installment</option>
                      <option value="Hold">Hold Booking</option>
                    </Field>
                  </div>

                  <div className="flex space-x-4">
                    {/* Total Plot Amount */}
                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Total Plot Amount:
                      </label>
                      <Field
                        type="text"
                        name="bookingAmount"
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                        value={formattedAmount}
                        onChange={(e) =>
                          handlePlotAmountChange(e, setFieldValue)
                        }
                        placeholder="Amount will auto-populate from plot price if available"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        💡 Auto-populates from plot price when plot is selected.
                        You can edit this amount manually.
                      </div>
                      <ErrorMessage
                        name="bookingAmount"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    {/* Amount in Words */}
                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Amount in Words:
                      </label>
                      <Field
                        type="text"
                        readOnly
                        name="bookingAmount"
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                        value={amountInWords}
                      />
                    </div>
                  </div>

                  {/* Installment Fields */}
                  {values.paymentMode === "Installment" && (
                    <>
                      <div>
                        <label
                          className={`block ${theme.tailwind.text9} font-medium`}
                        >
                          Initial Payment:
                        </label>
                        <Field
                          type="text"
                          name="initialPayment"
                          className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                          value={formattedInitialPayment}
                          onChange={(e) =>
                            handleInitialPaymentChange(e, setFieldValue)
                          }
                        />
                        <ErrorMessage
                          name="initialPayment"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      <div className="flex space-x-4">
                        <div className="flex-1">
                          <label
                            className={`block ${theme.tailwind.text9} font-medium`}
                          >
                            Installment Years:
                          </label>
                          <Field
                            as="select"
                            name="installmentYears"
                            className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                          >
                            <option value={0}>0 Years</option>
                            <option value={1}>1 Year</option>
                            <option value={2}>2 Years</option>
                            <option value={3}>3 Years</option>
                            <option value={4}>4 Years</option>
                            <option value={5}>5 Years</option>
                            <option value={6}>6 Years</option>
                            <option value={7}>7 Years</option>
                            <option value={8}>8 Years</option>
                            <option value={9}>9 Years</option>
                            <option value={10}>10 Years</option>
                          </Field>
                          <ErrorMessage
                            name="installmentYears"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>

                        <div className="flex-1">
                          <label
                            className={`block ${theme.tailwind.text9} font-medium`}
                          >
                            Installment Months:
                          </label>
                          <Field
                            as="select"
                            name="installmentMonths"
                            className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                          >
                            <option value={0}>0 Months</option>
                            <option value={1}>1 Month</option>
                            <option value={2}>2 Months</option>
                            <option value={3}>3 Months</option>
                            <option value={4}>4 Months</option>
                            <option value={5}>5 Months</option>
                            <option value={6}>6 Months</option>
                            <option value={7}>7 Months</option>
                            <option value={8}>8 Months</option>
                            <option value={9}>9 Months</option>
                            <option value={10}>10 Months</option>
                            <option value={11}>11 Months</option>
                          </Field>
                          <ErrorMessage
                            name="installmentMonths"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      </div>

                      {/* Draft Installment Plan */}
                      {installments.length > 0 && (
                        <div
                          className={`mt-4 p-4 rounded-lg border ${theme.tailwind.border7} ${theme.tailwind.bg2}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3
                              className={`text-lg font-semibold ${theme.tailwind.text9} flex items-center gap-2`}
                            >
                              <Calendar size={20} />
                              Draft Installment Plan
                            </h3>
                            <button
                              type="button"
                              onClick={() =>
                                openInstallmentPlanPrint(
                                  {
                                    plotNumber: selectedPlot?.plot_number,
                                    bookingDate: values.bookingDate,
                                    plotSize: selectedPlot?.size || selectedPlot?.marla,
                                    totalPrice: parseFloat(values.bookingAmount || 0),
                                    initialPayment: parseFloat(values.initialPayment || 0),
                                  },
                                  installments
                                )
                              }
                              className={`text-sm px-3 py-2 rounded-md border ${theme.tailwind.border7} ${theme.tailwind.bg5} ${theme.tailwind.text9} hover:${theme.tailwind.bgHover5}`}
                              title="Print draft installment plan"
                            >
                              Print
                            </button>
                          </div>
                          <div className="overflow-x-auto">
                            <table
                              className={`w-full ${theme.tailwind.bg6} rounded-lg overflow-hidden`}
                            >
                              <thead
                                className={`${theme.tailwind.bgr2} ${theme.tailwind.text9} uppercase font-semibold text-sm`}
                              >
                                <tr>
                                  <th className="py-2 px-4 border-b text-left">
                                    #
                                  </th>
                                  <th className="py-2 px-4 border-b text-left">
                                    Due Date
                                  </th>
                                  <th className="py-2 px-4 border-b text-right">
                                    Amount
                                  </th>
                                  <th className="py-2 px-4 border-b text-center">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-300">
                                {installments.map((inst, idx) => (
                                  <tr
                                    key={idx}
                                    className={`border-b ${
                                      idx % 2 === 0
                                        ? theme.tailwind.bg2
                                        : theme.tailwind.bg6
                                    }`}
                                  >
                                    <td className="py-2 px-4">
                                      {inst.installment_number}
                                    </td>
                                    <td className="py-2 px-4">
                                      {inst.due_date.toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                      })}
                                    </td>
                                    <td className="py-2 px-4 text-right font-semibold">
                                      {formatCurrency(inst.amount)}
                                    </td>
                                    <td className="py-2 px-4 text-center">
                                      <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-600">
                                        {inst.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot
                                className={`${theme.tailwind.bgr2} ${theme.tailwind.text9} font-semibold`}
                              >
                                <tr>
                                  <td
                                    colSpan="2"
                                    className="py-2 px-4 text-right"
                                  >
                                    Total:
                                  </td>
                                  <td className="py-2 px-4 text-right">
                                    {formatCurrency(
                                      installments.reduce(
                                        (sum, inst) => sum + inst.amount,
                                        0
                                      )
                                    )}
                                  </td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Remaining Balance Display */}
                  {(values.paymentMode === "Installment" ||
                    values.paymentMode === "Hold") && (
                    <div className="flex space-x-4">
                      <div className="flex-1">
                        <label
                          className={`block ${theme.tailwind.text9} font-medium`}
                        >
                          Remaining Balance:
                        </label>
                        <Field
                          type="text"
                          readOnly
                          name="remainingBalance"
                          className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                          value={formatCurrency(
                            calculateRemainingBalance(
                              parseFloat(values.bookingAmount || 0),
                              parseFloat(values.initialPayment || 0),
                              parseFloat(values.holdAmount || 0),
                              values.paymentMode
                            )
                          )}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-4">
                    {/* Receipt Number Field */}
                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Receipt Number:
                      </label>
                      <Field
                        type="text"
                        name="receiptNo"
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                        placeholder="Enter receipt number"
                      />
                      <ErrorMessage
                        name="receiptNo"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    {/* Booking Date Field */}
                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Booking Date:
                      </label>
                      <div className="w-full">
                        <DatePicker
                          selected={
                            values.bookingDate
                              ? (() => {
                                  const parsed = stringToLocalDate(
                                    values.bookingDate
                                  );
                                  return parsed &&
                                    !isNaN(parsed.getTime())
                                    ? parsed
                                    : null;
                                })()
                              : null
                          }
                          onChange={(date) => {
                            if (date && !isNaN(date.getTime())) {
                              setFieldValue("bookingDate", dateToString(date));
                            } else {
                              setFieldValue("bookingDate", "");
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="DD/MM/YYYY"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          wrapperClassName="w-full"
                          className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                        />
                      </div>
                      <ErrorMessage
                        name="bookingDate"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>

                  {/* Hold Booking Fields */}
                  {values.paymentMode === "Hold" && (
                    <>
                      <div className="flex space-x-4">
                        <div className="flex-1">
                          <label
                            className={`block ${theme.tailwind.text9} font-medium`}
                          >
                            Hold Amount:
                          </label>
                          <Field
                            type="text"
                            name="holdAmount"
                            className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                            placeholder="Enter hold amount"
                            value={formattedHoldAmount}
                            onChange={(e) =>
                              handleHoldAmountChange(e, setFieldValue)
                            }
                          />
                          <ErrorMessage
                            name="holdAmount"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>

                        <div className="flex-1">
                          <label
                            className={`block ${theme.tailwind.text9} font-medium`}
                          >
                            Hold Days:
                          </label>
                          <Field
                            type="number"
                            name="holdDays"
                            min="1"
                            max="30"
                            className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                            placeholder="Number of days to hold"
                          />
                          <ErrorMessage
                            name="holdDays"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Dealer Details */}
                  <h4 className={`${theme.tailwind.text} text-lg`}>
                    Dealer Details (Optional)
                  </h4>
                  <div className="flex space-x-6">
                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Dealer Commission:
                      </label>
                      <Field
                        type="text"
                        name="dealerCommission"
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                        value={dealerCommissionFormatted}
                        onChange={(e) =>
                          handleDealerCommissionChange(e, setFieldValue)
                        }
                      />
                      <ErrorMessage
                        name="dealerCommission"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    {/* Dealer Commission Date Field */}
                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Dealer Commission Date:
                      </label>
                      <DatePicker
                        selected={
                          values.dealerCommissionDate
                            ? (() => {
                                const parsed = stringToLocalDate(
                                  values.dealerCommissionDate
                                );
                                return parsed && !isNaN(parsed.getTime())
                                  ? parsed
                                  : null;
                              })()
                            : null
                        }
                        onChange={(date) => {
                          if (date && !isNaN(date.getTime())) {
                            setFieldValue(
                              "dealerCommissionDate",
                              dateToString(date)
                            );
                          } else {
                            setFieldValue("dealerCommissionDate", "");
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="DD/MM/YYYY"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        wrapperClassName="w-full"
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                      />
                    </div>

                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Dealer Name:
                      </label>
                      <Field
                        type="text"
                        name="dealerName"
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                        value={values.dealerName}
                        onChange={(e) =>
                          setFieldValue("dealerName", e.target.value)
                        }
                      />
                      <ErrorMessage
                        name="dealerName"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="flex-1">
                      <label
                        className={`block ${theme.tailwind.text9} font-medium`}
                      >
                        Dealer Phone:
                      </label>
                      <Field
                        type="text"
                        name="dealerPhone"
                        placeholder="+92 3XX-XXXXXXX"
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                        value={
                          values.dealerPhone
                            ? formatPhoneNumber(values.dealerPhone)
                            : ""
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "dealerPhone",
                            formatPhoneNumber(e.target.value)
                          )
                        }
                      />
                      <ErrorMessage
                        name="dealerPhone"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>

                  {/* Offer Tag Field */}
                  <div className="flex-1">
                    <label
                      className={`block ${theme.tailwind.text9} font-medium`}
                    >
                      Offer Tag:
                    </label>
                    <Field
                      type="text"
                      name="offerTag"
                      className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.bgFocus} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black transition`}
                      value={values.offerTag}
                      onChange={(e) => setFieldValue("offerTag", e.target.value)}
                    />
                    <ErrorMessage
                      name="offerTag"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className={`flex-1 py-2 px-4 rounded-md transition ${theme.tailwind.bg5} ${theme.tailwind.text9} ${theme.tailwind.bgHover5}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`flex-1 py-2 px-4 rounded-md transition ${
                        isSubmitting
                          ? `${theme.tailwind.bg5} ${theme.tailwind.text9}`
                          : `${theme.tailwind.bg13} text-white shadow-lg ${theme.tailwind.shadowing} ${theme.tailwind.bgHover5}`
                      }`}
                    >
                      {isSubmitting ? "Creating Booking..." : "Create Booking"}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>,
    document.body
  );
};

const BookingForm = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { users, status } = useSelector((state) => state.auth);
  // console.log(users)
  const { activeSociety, theme, gradient, setActiveSociety } =
    useSidebarContext();
  // console.log(activeSociety.users)
  // State for plot selection and customer search
  const [plotId, setPlotId] = useState("");
  const [localPlots, setLocalPlots] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [formattedAmount, setFormattedAmount] = useState("");
  const [amountInWords, setAmountInWords] = useState("");
  const [dealerCommissionFormatted, setDealerCommissionFormatted] =
    useState("");
  const [formattedInitialPayment, setFormattedInitialPayment] = useState("");
  const [formattedHoldAmount, setFormattedHoldAmount] = useState("");
  const [installmentYears, setInstallmentYears] = useState("");
  const [installmentMonths, setInstallmentMonths] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const plotsPerPage = 10;

  const [filters, setFilters] = useState({
    plotNumber: "",
    block: "",
    size: "",
    marla: "",
    category: "",
    booking_status: "",
    type: "",
  });
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Fetch required data on component load
  useEffect(() => {
    dispatch(getAllBookings());
    if (status === "idle" || (status === "failed" && users.length === 0)) {
      dispatch(fetchAllUsers());
    }
    dispatch(fetchSocieties());
  }, [dispatch, status, users.length]);

  // Listen for plot updates via socket events
  useEffect(() => {
    const handlePlotUpdated = (event) => {
      const { plotId, booking_status, status } = event.detail || {};
      if (plotId && localPlots.length > 0) {
        // Update the specific plot in localPlots
        setLocalPlots((prevPlots) => {
          const updatedPlots = prevPlots.map((plot) =>
            plot._id === plotId
              ? {
                  ...plot,
                  booking_status: booking_status || plot.booking_status,
                  status: status || plot.status,
                }
              : plot
          );
          return updatedPlots.sort(
            (a, b) => a.plot_number - b.plot_number
          );
        });
      } else {
        // If no specific plotId, refresh all plots by fetching societies
        dispatch(fetchSocieties(true));
      }
    };

    // Listen for plotUpdated events from socket
    window.addEventListener('plotUpdated', handlePlotUpdated);

    return () => {
      window.removeEventListener('plotUpdated', handlePlotUpdated);
    };
  }, [localPlots, dispatch]);

  useEffect(() => {
    if (activeSociety?.plots) {
      // Sort plots by their plot number in ascending order before setting them
      const sortedPlots = [...activeSociety.plots].sort((a, b) => {
        if (a.plot_number && b.plot_number) {
          return a.plot_number - b.plot_number;
        }
        return 0; // Handle cases where plotNumber might be missing
      });
      setLocalPlots(sortedPlots);

      // Debug logging for plot prices
      // console.log(
      //   'Active Society Plots Updated:',
      //   sortedPlots.map(plot => ({
      //     plot_number: plot.plot_number,
      //     price: plot.price,
      //     marla: plot.marla,
      //     category: plot.category
      //   }))
      // )

      // Check if price field exists in plot objects
      const plotsWithPrice = sortedPlots.filter((plot) =>
        plot.hasOwnProperty("price")
      );
      const plotsWithoutPrice = sortedPlots.filter(
        (plot) => !plot.hasOwnProperty("price")
      );

      // console.log('Plots WITH price field:', plotsWithPrice.length)
      // console.log('Plots WITHOUT price field:', plotsWithoutPrice.length)

      if (plotsWithoutPrice.length > 0) {
        // console.log('Sample plot without price:', plotsWithoutPrice[0])
        // console.log('All plot fields:', Object.keys(plotsWithoutPrice[0]))
      }
    }
  }, [activeSociety?.plots, activeSociety?._id]); // Added activeSociety._id to trigger refresh when society changes

  // console.log('Active Society:', activeSociety?.name)
  // console.log('Active Society Plots:', activeSociety?.plots)
  // console.log('Local Plots:', localPlots)
  const BookingValidationSchema = Yup.object().shape({
    plot: Yup.string().required("Please select a plot"),
    customer: Yup.string().required("Please select a customer"),
    paymentMode: Yup.string()
      .oneOf(["Full", "Installment", "Hold"], "Invalid payment mode")
      .required("Payment mode is required"),
    bookingAmount: Yup.number()
      .positive("Amount must be positive")
      .required("Total plot amount is required"),

    initialPayment: Yup.number().when("paymentMode", {
      is: "Installment",
      then: (schema) =>
        schema
          .required("Initial payment is required")
          .max(
            Yup.ref("bookingAmount"),
            "Initial payment cannot be more than total amount"
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    installmentYears: Yup.number().when("paymentMode", {
      is: "Installment",
      then: (schema) =>
        schema
          .min(0, "Years must be 0 or greater")
          .max(10, "Years cannot exceed 10")
          .test(
            "is-valid-installment-duration",
            "At least one field (years or months) must be filled",
            function (value) {
              const { installmentMonths } = this.parent;
              return value > 0 || installmentMonths > 0;
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    installmentMonths: Yup.number().when("paymentMode", {
      is: "Installment",
      then: (schema) =>
        schema
          .min(0, "Months must be 0 or greater")
          .max(11, "Months cannot exceed 11")
          .test(
            "is-valid-installment-duration",
            "At least one field (years or months) must be filled",
            function (value) {
              const { installmentYears } = this.parent;
              return value > 0 || installmentYears > 0;
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    holdAmount: Yup.number().when("paymentMode", {
      is: "Hold",
      then: (schema) =>
        schema
          .required("Hold amount is required")
          .positive("Hold amount must be positive")
          .max(
            Yup.ref("bookingAmount"),
            "Hold amount cannot be more than total amount"
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    holdDays: Yup.number().when("paymentMode", {
      is: "Hold",
      then: (schema) =>
        schema
          .required("Hold days is required")
          .positive("Hold days must be positive")
          .max(30, "Hold days cannot exceed 30"),
      otherwise: (schema) => schema.notRequired(),
    }),

    receiptNo: Yup.string()
      .required("Receipt number is required")
      .max(100, "Receipt number cannot exceed 100 characters"),
    offerTag: Yup.string(),
    bookingDate: Yup.string()
      .test("is-valid-date", "Invalid date", (value) => {
        if (!value) return true; // Allow null/undefined/empty
        try {
          // Check format first
          if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            return false;
          }
          const [day, month, year] = value.split("/").map(Number);
          // Validate ranges
          if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
            return false;
          }
          const date = new Date(year, month - 1, day);
          // Check if date is valid and matches input
          if (isNaN(date.getTime())) {
            return false;
          }
          return (
            date.getDate() === day &&
            date.getMonth() + 1 === month &&
            date.getFullYear() === year
          );
        } catch (error) {
          return false;
        }
      })
      .nullable(),
    dealerCommissionDate: Yup.string()
      .test("is-valid-date", "Invalid date", (value) => {
        if (!value) return true; // Allow null/undefined/empty
        try {
          // Check format first
          if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            return false;
          }
          const [day, month, year] = value.split("/").map(Number);
          // Validate ranges
          if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
            return false;
          }
          const date = new Date(year, month - 1, day);
          // Check if date is valid and matches input
          if (isNaN(date.getTime())) {
            return false;
          }
          return (
            date.getDate() === day &&
            date.getMonth() + 1 === month &&
            date.getFullYear() === year
          );
        } catch (error) {
          return false;
        }
      })
      .nullable(),
  });

  const formatNumberWithCommas = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0";
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(amount)
      .replace("PKR", "")
      .trim();
  };

  const calculateRemainingBalance = (
    totalAmount,
    initialPayment,
    holdAmount,
    paymentMode
  ) => {
    if (paymentMode === "Full") return 0;
    if (paymentMode === "Installment")
      return totalAmount - (initialPayment || 0);
    if (paymentMode === "Hold") return totalAmount - (holdAmount || 0);
    return totalAmount;
  };

  const handlePlotAmountChange = (e, setFieldValue) => {
    let value = e.target.value.replace(/,/g, "");
    const formatted = formatNumberWithCommas(value);
    setFormattedAmount(formatted);
    setFieldValue("bookingAmount", value);

    // Convert number to words for total plot amount
    const words = toWords(Number(value));
    setAmountInWords(
      `${formatted} (${words.charAt(0).toUpperCase() + words.slice(1)})`
    );
  };

  const handleInitialPaymentChange = (e, setFieldValue) => {
    let value = e.target.value.replace(/,/g, "");
    const formatted = formatNumberWithCommas(value);
    setFieldValue("initialPayment", value);
    setFormattedInitialPayment(formatted);
  };

  const handleDealerCommissionChange = (e, setFieldValue) => {
    let value = e.target.value.replace(/,/g, "");
    const formatted = formatNumberWithCommas(value);
    setDealerCommissionFormatted(formatted);
    setFieldValue("dealerCommission", value);
  };

  const handleHoldAmountChange = (e, setFieldValue) => {
    let value = e.target.value.replace(/,/g, "");
    const formatted = formatNumberWithCommas(value);
    setFormattedHoldAmount(formatted);
    setFieldValue("holdAmount", value);
  };

  const handlePlotSelect = (id, setFieldValue = null) => {
    const newPlotId = plotId === id ? "" : id;
    setPlotId(newPlotId);
    if (setFieldValue) {
      setFieldValue("plot", newPlotId);
    }

    // Auto-populate Total Plot Amount with plot price if available
    if (newPlotId) {
      const selectedPlot = localPlots.find((plot) => plot._id === newPlotId);
      if (selectedPlot && selectedPlot.price) {
        // Auto-populate with plot price
        const priceValue = selectedPlot.price.toString();
        setFormattedAmount(formatNumberWithCommas(priceValue));
        if (setFieldValue) {
          setFieldValue("bookingAmount", priceValue);
        }

        // Convert number to words for total plot amount
        const words = toWords(Number(priceValue));
        setAmountInWords(
          `${formatNumberWithCommas(priceValue)} (${
            words.charAt(0).toUpperCase() + words.slice(1)
          })`
        );
      } else {
        // No price available, clear the field for user to fill
        setFormattedAmount("");
        if (setFieldValue) {
          setFieldValue("bookingAmount", "");
        }
        setAmountInWords("");
      }
      // Open modal when plot is selected
      setIsModalOpen(true);
    } else {
      // No plot selected, clear the field and close modal
      setFormattedAmount("");
      if (setFieldValue) {
        setFieldValue("bookingAmount", "");
      }
      setAmountInWords("");
      setIsModalOpen(false);
    }
  };

  const filteredPlots = localPlots.filter((plot) => {
    return (
      (!filters.plotNumber || plot.plot_number.includes(filters.plotNumber)) &&
      (!filters.block || plot.block.includes(filters.block)) &&
      (!filters.size || plot.size.includes(filters.size)) &&
      (!filters.marla || plot.marla.includes(filters.marla)) &&
      (!filters.category || plot.category === filters.category) &&
      (!filters.booking_status ||
        plot.booking_status === filters.booking_status) &&
      (!filters.type || plot.plot_type === filters.type)
    );
  });
  const sortedPlots = [...filteredPlots].sort((a, b) => {
    if (a.plot_number && b.plot_number) {
      return a.plot_number
        .toString()
        .localeCompare(b.plot_number.toString(), undefined, {
          numeric: true,
          sensitivity: "base",
        });
    }
    return 0;
  });
  const totalPages = Math.ceil(sortedPlots.length / plotsPerPage);
  const currentPlots = sortedPlots.slice(
    (currentPage - 1) * plotsPerPage,
    currentPage * plotsPerPage
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const renderPageButtons = () => {
    const getPageNumbers = () => {
      if (totalPages <= 3)
        return Array.from({ length: totalPages }, (_, i) => i + 1);

      if (currentPage <= 2) return [1, 2, 3];
      if (currentPage >= totalPages - 1)
        return [totalPages - 2, totalPages - 1, totalPages];

      return [currentPage - 1, currentPage, currentPage + 1];
    };

    const pageNumbers = getPageNumbers();

    return pageNumbers.map((pageNum) => (
      <button
        key={pageNum}
        onClick={() => setCurrentPage(pageNum)}
        className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${
          pageNum === currentPage
            ? `${theme.tailwind.bg13} text-white shadow-lg scale-110`
            : `bg-white/10 ${theme.tailwind.text9} hover:bg-slate-400 hover:text-white`
        }`}
      >
        {pageNum}
      </button>
    ));
  };

  // useEffect(() => {
  //   console.log("Active Society Users:", activeSociety?.users);
  // }, [activeSociety]);

  const filteredUsers = (activeSociety?.users || [])
    .filter((user) => user.role === "Customer")
    .filter((user) =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const handleSelectCustomer = (user, setFieldValue) => {
    setSearchQuery(user.username);
    setSelectedCustomer(user);
    setFieldValue("customer", user._id);
    setIsDropdownOpen(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const formatPhoneNumber = (phone) => {
    let cleaned = ("" + phone).replace(/\D/g, "");
    if (cleaned.startsWith("92")) {
      cleaned = "92" + cleaned.slice(2);
    }
    if (cleaned.length > 3 && cleaned.length <= 5) {
      return `+92 ${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
    } else if (cleaned.length > 5) {
      return `+92 ${cleaned.slice(2, 5)}-${cleaned.slice(5, 12)}`;
    }
    return `+92 ${cleaned.slice(2)}`;
  };

  const selectedPlot = currentPlots.find((plot) => plot._id === plotId);
  const showAlert =
    selectedPlot &&
    (selectedPlot.category === "Corner" ||
      selectedPlot.category === "Boulevard");

  const resetAllFields = (resetForm, setFieldValue) => {
    resetForm({
      values: {
        plot: "",
        customer: "",
        paymentMode: "Full",
        bookingAmount: "",
        dealerCommission: "",
        dealerPhone: "",
        dealerName: "",
        initialPayment: "",
        installmentYears: "",
        receiptNo: "",
        offerTag: "",
        bookingDate: "",
        dealerCommissionDate: "",
        holdAmount: "",
        holdDays: "",
      },
    });
    setPlotId("");
    setSearchQuery("");
    setSelectedCustomer(null);
    setIsDropdownOpen(false);
    setFormattedAmount("");
    setAmountInWords("");
    setDealerCommissionFormatted("");
    setFormattedInitialPayment("");
    setFormattedHoldAmount("");
    setInstallmentYears("");
    setInstallmentMonths("");
    setCurrentPage(1);
    setIsModalOpen(false);
  };

  // Calculate draft installment plan based on backend logic
  const calculateDraftInstallmentPlan = (values) => {
    if (values.paymentMode !== "Installment") return [];

    const totalAmount = parseFloat(values.bookingAmount || 0);
    const initialPayment = parseFloat(values.initialPayment || 0);
    const remainingBalance = totalAmount - initialPayment;

    if (remainingBalance <= 0) return [];

    const installmentYears = parseFloat(values.installmentYears || 0);
    const installmentMonths = parseFloat(values.installmentMonths || 0);
    const totalInstallmentYears = installmentYears + installmentMonths / 12;
    const totalMonths = Math.round(totalInstallmentYears * 12);

    if (totalMonths <= 0) return [];

    const monthlyInstallmentAmount = Number(
      (remainingBalance / totalMonths).toFixed(2)
    );

    const lastInstallmentAmount = Number(
      (
        remainingBalance -
        monthlyInstallmentAmount * (totalMonths - 1)
      ).toFixed(2)
    );

    const bookingDate = values.bookingDate
      ? stringToLocalDate(values.bookingDate)
      : new Date();

    if (!bookingDate || isNaN(bookingDate.getTime())) {
      return [];
    }

    const installments = [];
    for (let i = 1; i <= totalMonths; i++) {
      const dueDate = new Date(bookingDate);
      dueDate.setMonth(dueDate.getMonth() + i);

      const installmentAmount =
        i === totalMonths ? lastInstallmentAmount : monthlyInstallmentAmount;

      installments.push({
        installment_number: i,
        due_date: dueDate,
        amount: installmentAmount,
        status: "Pending",
      });
    }

    return installments;
  };

  const stringToLocalDate = (dateString) => {
    if (!dateString) return null;
    try {
      const [day, month, year] = dateString.split("/").map(Number);
      // Validate the parsed values
      if (!day || !month || !year || isNaN(day) || isNaN(month) || isNaN(year)) {
        return null;
      }
      // Validate month and day ranges
      if (month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
      }
      const date = new Date(year, month - 1, day);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch (error) {
      console.error('Error parsing date string:', error, dateString);
      return null;
    }
  };

  const dateToString = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (!activeSociety) {
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${gradient}`}
      >
        <div
          className={`bg-black/40 border ${theme.tailwind.border5} px-12 py-10 rounded-2xl shadow-2xl transform transition-all hover:scale-105 hover:shadow-4xl`}
        >
          <h2
            className={`text-4xl font-extrabold mb-6 ${theme.tailwind.text9} tracking-wide ${theme.tailwind.drop_shadow}`}
          >
            No Active Society
          </h2>
          <p
            className={`text-xl ${theme.tailwind.text} opacity-90 tracking-wider leading-relaxed`}
          >
            Please select an active society before creating a booking.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`p-8 py-12 px-6 ${gradient} rounded-2xl ${theme.tailwind.shadows} min-h-screen`}
    >
      <div className="max-w-7xl mx-auto space-y-8">
        <div
          className={`${gradient} shadow-2xl rounded-2xl border-2 ${theme.tailwind.border6} p-10 transform transition-all hover:scale-[1] hover:shadow-3xl`}
        >
          <div className="flex justify-between items-center">
            <h2
              className={`text-5xl font-bold text-transparent bg-clip-text ${theme.tailwind.text8} mb-8 uppercase tracking-wide`}
            >
              {activeSociety?.name || "Booking"} Booking
            </h2>
            <button
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              className={`group relative flex items-center gap-3 px-6 py-3 ${theme.tailwind.bg10} ${theme.tailwind.text} rounded-xl ${theme.tailwind.bgHover3} transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:ring-2 ${theme.tailwind.ringHover}`}
            >
              <div
                className={`absolute inset-0 ${theme.tailwind.bg11} rounded-xl opacity-0 group-hover:opacity-100 transition-opacity`}
              ></div>
              <span className="relative z-10">
                {isFiltersExpanded ? <X size={24} /> : <Filter size={24} />}
              </span>
              <span className="relative z-10 font-semibold">
                {isFiltersExpanded ? "Close Filters" : "Open Filters"}
              </span>
            </button>
          </div>

          {isFiltersExpanded && (
            <div
              className={`grid grid-cols-3 gap-4 ${theme.tailwind.bg2} p-6 rounded-xl border ${theme.tailwind.border7} mb-6 animate-slide-down`}
            >
              <div className="relative">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Plot Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="plotNumber"
                    value={filters.plotNumber}
                    onChange={handleFilterChange}
                    placeholder="Search Plot Number"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${theme.tailwind.bg5} border ${theme.tailwind.border8} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                  />
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                    size={20}
                  />
                </div>
              </div>
              <div className="relative">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Plot Block
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="block"
                    value={filters.block}
                    onChange={handleFilterChange}
                    placeholder="Search Block"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${theme.tailwind.bg5} border ${theme.tailwind.border8} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                  />
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                    size={20}
                  />
                </div>
              </div>
              <div className="relative">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Plot Size
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="size"
                    value={filters.size}
                    onChange={handleFilterChange}
                    placeholder="Search Size"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${theme.tailwind.bg5} border ${theme.tailwind.border8} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                  />
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                    size={20}
                  />
                </div>
              </div>
              <div className="relative">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Plot Marla
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="marla"
                    value={filters.marla}
                    onChange={handleFilterChange}
                    placeholder="Search Size"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${theme.tailwind.bg5} border ${theme.tailwind.border8} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                  />
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                    size={20}
                  />
                </div>
              </div>
              <div className="relative">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Plot Category
                </label>
                <div className="relative">
                  <select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${theme.tailwind.bg5} border ${theme.tailwind.border8} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                  >
                    <option value="">All Categories</option>
                    <option value="General">General</option>
                    <option value="Corner">Corner</option>
                    <option value="Park Face">Park Face</option>
                    <option value="Boulevard">Boulevard</option>
                  </select>
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                    size={20}
                  />
                </div>
              </div>
              <div className="relative">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Plot Booking Status
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="booking_status"
                    value={filters.booking_status}
                    onChange={handleFilterChange}
                    placeholder="Search Status"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${theme.tailwind.bg5} border ${theme.tailwind.border8} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                  />
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                    size={20}
                  />
                </div>
              </div>
              <div className="relative">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Plot Type
                </label>
                <div className="relative">
                  <select
                    name="type"
                    value={filters.type}
                    onChange={handleFilterChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${theme.tailwind.bg5} border ${theme.tailwind.border8} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                  >
                    <option value="">All Types</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                  </select>
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                    size={20}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Booking Modal */}
          {isModalOpen && plotId && (
            <BookingModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setPlotId("");
              }}
              plotId={plotId}
              selectedPlot={currentPlots.find((plot) => plot._id === plotId)}
              activeSociety={activeSociety}
              theme={theme}
              gradient={gradient}
              users={users}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedCustomer={selectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              filteredUsers={filteredUsers}
              formattedAmount={formattedAmount}
              setFormattedAmount={setFormattedAmount}
              amountInWords={amountInWords}
              setAmountInWords={setAmountInWords}
              dealerCommissionFormatted={dealerCommissionFormatted}
              setDealerCommissionFormatted={setDealerCommissionFormatted}
              formattedInitialPayment={formattedInitialPayment}
              setFormattedInitialPayment={setFormattedInitialPayment}
              formattedHoldAmount={formattedHoldAmount}
              setFormattedHoldAmount={setFormattedHoldAmount}
              handlePlotAmountChange={handlePlotAmountChange}
              handleInitialPaymentChange={handleInitialPaymentChange}
              handleDealerCommissionChange={handleDealerCommissionChange}
              handleHoldAmountChange={handleHoldAmountChange}
              handleSelectCustomer={handleSelectCustomer}
              formatPhoneNumber={formatPhoneNumber}
              formatCurrency={formatCurrency}
              calculateRemainingBalance={calculateRemainingBalance}
              stringToLocalDate={stringToLocalDate}
              dateToString={dateToString}
              resetAllFields={resetAllFields}
              calculateDraftInstallmentPlan={calculateDraftInstallmentPlan}
              BookingValidationSchema={BookingValidationSchema}
              dispatch={dispatch}
              enqueueSnackbar={enqueueSnackbar}
              createBooking={createBooking}
              getAllBookings={getAllBookings}
              setActiveSociety={setActiveSociety}
              setLocalPlots={setLocalPlots}
              localPlots={localPlots}
            />
          )}

          {/* Plots Table */}
          <div className="space-y-4">
                  <div
                    className={`overflow-x-auto rounded-xl shadow-2xl backdrop-blur-sm border-2 ${theme.tailwind.border7}`}
                  >
                    <div
                      className={`p-6 ${theme.tailwind.bg2} border-b-2 ${theme.tailwind.border7}`}
                    >
                      <div className="flex justify-between items-center">
                        <h3
                          className={`text-3xl font-semibold text-transparent bg-clip-text ${theme.tailwind.text8}`}
                        >
                          Plots Detail
                        </h3>
                        <div className="flex items-center gap-4">
                          {/* Price Summary */}
                          <div
                            className={`text-sm ${theme.tailwind.text9} bg-opacity-20 ${theme.tailwind.bg5} px-3 py-2 rounded-lg`}
                          >
                            <span className="text-green-400">✓</span>{" "}
                            {currentPlots.filter((p) => p.price).length} plots
                            with prices |
                            <span className="text-gray-400"> —</span>{" "}
                            {currentPlots.filter((p) => !p.price).length} plots
                            need pricing
                          </div>
                          <button
                            onClick={() => {
                              // Force refresh of societies data
                              dispatch(fetchSocieties(true));
                              enqueueSnackbar("Refreshing plot data...", {
                                variant: "info",
                              });
                            }}
                            className={`px-4 py-2 rounded-lg ${theme.tailwind.bg13} text-white hover:opacity-80 transition-all duration-300 flex items-center gap-2`}
                            title="Refresh plot data (useful after updating pricing)"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                            Refresh
                          </button>
                        </div>
                      </div>
                    </div>
                    <table
                      className={`min-w-full ${theme.tailwind.bg6} shadow-lg rounded-lg overflow-hidden`}
                    >
                      <thead
                        className={`${theme.tailwind.bgr2} ${theme.tailwind.text9} uppercase font-semibold`}
                      >
                        <tr>
                          {[
                            "Plot Number",
                            "Type",
                            "Block",
                            "Size (sq.ft)",
                            "Marla",
                            "Price",
                            "Category",
                            "Booking Status",
                            "Status",
                            "Action",
                          ].map((header) => (
                            <th
                              key={header}
                              className={`py-4 px-6 border-b ${theme.tailwind.border7} text-left`}
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="text-gray-300">
                        {currentPlots.map((plot, index) => (
                          <tr
                            key={plot._id}
                            onClick={() => {
                              if (
                                plot.booking_status !== "Sold" &&
                                plot.booking_status !== "Booked" &&
                                plot.booking_status !== "Transfer"
                              ) {
                                handlePlotSelect(plot._id);
                              }
                            }}
                            className={`border-b ${
                              theme.tailwind.border9
                            } transition-colors duration-300 ${
                              plot.booking_status === "Sold" ||
                              plot.booking_status === "Booked" ||
                              plot.booking_status === "Transfer" ||
                              plot.booking_status === "Hold"
                                ? `${theme.tailwind.bg12} text-gray-400 cursor-not-allowed`
                                : index % 2 === 0
                                ? `${theme.tailwind.bg2}`
                                : `${theme.tailwind.bg6}`
                            } ${theme.tailwind.bgHover4}`}
                          >
                            {[
                              plot.plot_number,
                              plot.plot_type,
                              plot.block,
                              plot.size
                                ? formatNumberWithCommas(plot.size)
                                : plot.size,
                              plot.marla
                                ? formatNumberWithCommas(plot.marla)
                                : plot.marla,
                              plot.price ? (
                                <div className="flex items-center">
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs bg-red-400 ${theme.tailwind.text9} font-semibold`}
                                  >
                                    {formatCurrency(plot.price)}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs bg-gray-500 ${theme.tailwind.text9} font-semibold`}
                                  >
                                    N/A
                                  </span>
                                </div>
                              ),
                              plot.category,
                              plot.booking_status,
                              plot.status,
                            ].map((value, colIndex) => (
                              <td
                                key={colIndex}
                                className={`py-4 px-6 text-left ${
                                  colIndex === 6 || colIndex === 7
                                    ? "text-sm font-semibold"
                                    : `${theme.tailwind.text9}`
                                }`}
                              >
                                {colIndex === 5 ? (
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs ${
                                      value === "Booked"
                                        ? "bg-yellow-100 text-yellow-600"
                                        : value === "Hold"
                                        ? "bg-orange-100 text-orange-600"
                                        : "bg-green-100 text-green-600"
                                    }`}
                                  >
                                    {value}
                                  </span>
                                ) : colIndex === 7 ? (
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs ${
                                      value === "Available"
                                        ? "bg-green-100 text-green-600"
                                        : "bg-red-100 text-red-600"
                                    }`}
                                  >
                                    {value}
                                  </span>
                                ) : colIndex === 5 ? (
                                  <span className="font-semibold text-green-600">
                                    {value}
                                  </span>
                                ) : (
                                  value
                                )}
                              </td>
                            ))}
                            <td className="py-4 px-6">
                              <button
                                type="button"
                                onClick={() =>
                                  plot.booking_status !== "Sold" &&
                                  plot.booking_status !== "Booked" &&
                                  plot.booking_status !== "Transfer" &&
                                  plot.booking_status !== "Hold" &&
                                  handlePlotSelect(plot._id)
                                }
                                disabled={
                                  plot.booking_status === "Sold" ||
                                  plot.booking_status === "Booked" ||
                                  plot.booking_status === "Transfer" ||
                                  plot.booking_status === "Hold"
                                }
                                className={`text-sm font-semibold rounded px-3 py-1 transition-all duration-300 ${
                                  plot.booking_status === "Sold" ||
                                  plot.booking_status === "Booked" ||
                                  plot.booking_status === "Transfer" ||
                                  plot.booking_status === "Hold"
                                    ? `${theme.tailwind.bg12} text-gray-600 cursor-not-allowed`
                                    : plotId === plot._id
                                    ? "bg-red-500 text-white hover:bg-red-600"
                                    : `${theme.tailwind.bg13} text-white ${theme.tailwind.bgHover5}`
                                }`}
                              >
                                {plotId === plot._id ? "Cancel" : "Select"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex justify-center items-center gap-3 sm:gap-4 mt-4 sm:mt-6 mb-4 sm:mb-5 p-2 sm:p-4">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className={`group flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full transition-all duration-300 ${
                          currentPage === 1
                            ? "bg-gray-600 cursor-not-allowed"
                            : `bg-white/10 ${theme.tailwind.bgHover5}`
                        }`}
                      >
                        <ChevronLeft
                          className={`w-3 h-3 sm:w-4 sm:h-4 ${
                            currentPage === 1
                              ? "text-gray-400"
                              : `${theme.tailwind.text9} group-hover:text-white`
                          }`}
                        />
                      </button>

                      <div className="flex items-center gap-1 sm:gap-2">
                        {currentPage > 2 && totalPages > 3 && (
                          <>
                            <button
                              onClick={() => setCurrentPage(1)}
                              className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover6} hover:text-white transition-all duration-300`}
                            >
                              1
                            </button>
                            <span className={`${theme.tailwind.text9} px-1`}>
                              ...
                            </span>
                          </>
                        )}

                        {renderPageButtons()}

                        {currentPage < totalPages - 1 && totalPages > 3 && (
                          <>
                            <span className={`${theme.tailwind.text9} px-1`}>
                              ...
                            </span>
                            <button
                              onClick={() => setCurrentPage(totalPages)}
                              className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover6} hover:text-white transition-all duration-300`}
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>

                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={`group flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full transition-all duration-300 ${
                          currentPage === totalPages
                            ? "bg-gray-600 cursor-not-allowed"
                            : `bg-white/10 ${theme.tailwind.bgHover5}`
                        }`}
                      >
                        <ChevronRight
                          className={`w-3 h-3 sm:w-4 sm:h-4 ${
                            currentPage === totalPages
                              ? "text-gray-400"
                              : `${theme.tailwind.text9} group-hover:text-white`
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
